// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Trends Chart Component
// Daily sessions and leads visualization
// ═══════════════════════════════════════════════════════════════════════════════

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatUiDate } from '@/lib/ui-format';

interface TrendItem {
  date: string;
  sessions: number;
  leads: number;
  conversionRate: number;
}

interface TrendsChartProps {
  data: TrendItem[];
}

interface TrendsTooltipPayloadItem {
  name?: string;
  value?: number;
}

interface TrendsTooltipProps {
  active?: boolean;
  payload?: TrendsTooltipPayloadItem[];
  label?: string;
}

const TrendsTooltip = ({ active, payload, label }: TrendsTooltipProps): JSX.Element | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-elevation-2">
      <p className="text-xs font-semibold text-foreground">{label}</p>
      <div className="mt-1 space-y-1 text-xs text-muted-foreground">
        {payload.map((item) => (
          <div key={item.name} className="flex items-center justify-between gap-3">
            <span>{item.name}</span>
            <span className="text-foreground">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export function TrendsChart({ data }: TrendsChartProps) {
  if (data.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">Daily Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No trend data available
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    date: formatUiDate(item.date, {
      month: 'short',
      day: 'numeric',
      year: undefined,
    }),
    Sessions: item.sessions,
    Leads: item.leads,
    'Conv %': item.conversionRate,
  }));

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Daily Trends</CardTitle>
        <p className="text-sm text-muted-foreground">
          Last {data.length} days performance
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              className="text-xs"
              tick={{ fill: 'var(--muted-foreground)' }}
            />
            <YAxis
              yAxisId="left"
              className="text-xs"
              tick={{ fill: 'var(--muted-foreground)' }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              className="text-xs"
              tick={{ fill: 'var(--muted-foreground)' }}
            />
            <Tooltip content={<TrendsTooltip />} />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="Sessions"
              stroke="var(--color-status-info)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-status-info)', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="Leads"
              stroke="var(--color-status-success)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-status-success)', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="Conv %"
              stroke="var(--color-status-warning)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-status-warning)', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
